import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { useFormContext, useWatch } from 'react-hook-form';
import t from 'react-translate';
import { NvFroala } from 'froala/components/nv-froala';
import { FroalaViewMode, UploadType } from 'froala/helpers/nv-froala-constants';
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { gray4, gray6 } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { config } from '@config/config.json';
import { ContactLearnersFormValues } from './contact-learners-form';

const styles = css`
  .email-section-header {
    border-top: 2px solid ${gray4};
    border-bottom: 1px solid ${gray4};
  }

  .push-section {
    .push-header {
      border-top: 2px solid ${gray6};
      border-bottom: 1px solid ${gray6};
    }

    .push-input {
      visibility: hidden;
      height: 0;
    }

    &.include-push {
      .push-input {
        visibility: visible;
        height: auto;
        margin-top: ${standardSpacing}px;
      }
    }
  }
`;

/**
 * Step 1 body — renders inside the modal only while step === 'write'.
 * Reads/writes RHF form via useFormContext (FormProvider is owned by the modal).
 * NvFroala is mounted here and NEVER rendered hidden behind display:none.
 */
const StepWriteMessage = () => {
  const { control } = useFormContext<ContactLearnersFormValues>();
  const includePushNotification = useWatch({ control, name: 'includePushNotification' });

  const pushRef = useRef<HTMLTextAreaElement>(null);
  const wasPushEnabled = useRef(includePushNotification);

  useEffect(() => {
    // Focus the push field only when the user turns the toggle on (off → on), not on
    // initial open where it already defaults to on (which would steal focus from the subject).
    const turnedOn = includePushNotification && !wasPushEnabled.current;
    wasPushEnabled.current = includePushNotification;
    if (!turnedOn) return undefined;
    const timeoutId = setTimeout(() => pushRef.current?.focus());
    return () => clearTimeout(timeoutId);
  }, [includePushNotification]);

  return (
    <div css={styles}>
      <div className='email-section-header d-flex align-items-center py-4 mt-4 mb-4'>
        <span className='font-size-large font-weight-bolder'>{t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.EMAIL_SECTION()}</span>
      </div>
      <div className='mb-4'>
        <NvTextInput
          withForm
          name='subject'
          required
          maxLength={255}
          autoComplete='off'
          placeholder={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.SUBJECT_PLACEHOLDER()}
        />
      </div>
      <div className='mb-4'>
        <NvFroala
          preset={FroalaViewMode.NORMAL}
          withCodeView
          withInsertTag
          withForm
          name='body'
          placeholder={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.BODY_PLACEHOLDER()}
          uploadType={UploadType.IMAGE_ONLY}
          extraTags={[]}
          fileExtensions={config.files.rte.images.common}
        />
      </div>
      <div className={`push-section mb-4 ${includePushNotification ? 'include-push' : ''}`}>
        <div className='push-header d-flex align-items-center font-weight-bolder py-4 px-0'>
          <NvCheckbox
            withForm
            name='includePushNotification'
            label={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.PUSH_LABEL()}
            labelClassName='font-size-large font-weight-bolder'
          />
        </div>
        <div className='push-input'>
          <NvTextArea
            withForm
            ref={pushRef}
            name='pushNotificationBody'
            ariaLabel={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.PUSH_ARIA_LABEL()}
            placeholder={t.LEARNER_PROGRESS.CONTACT_BAR.MODAL.PUSH_PLACEHOLDER()}
            errorOnTouching={false}
          />
        </div>
      </div>
    </div>
  );
};

export default StepWriteMessage;
